import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Grid,
  Typography,
  Chip,
  Button,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  Stack,
  alpha,
  useTheme,
  Paper,
  IconButton,
  Tooltip,
  CircularProgress,
  Switch,
  TextField,
  InputAdornment,
  Snackbar,
} from '@mui/material';
import {
  Email,
  Phone,
  Assignment,
  Receipt,
  ContactPhone,
  LocationOn,
  Refresh,
  ChevronRight,
  AccountBalanceWallet,
  Add,
  SupportAgent,
  Chat as ChatIcon,
  HeadsetMic,
  Person,
  ContentCopy,
  CheckCircle,
  VpnKey,
  Visibility,
  VisibilityOff,
  Save,
  OpenInNew,
  Edit,
  WhatsApp,
  InfoOutlined,
  Lock,
} from '@mui/icons-material';

import { Client, Contract, Invoice, Contact } from '../../types';
import { clientService } from '../../services/clientService';
import { contractService } from '../../services/contractService';
import invoiceService from '../../services/invoiceService';
import { contactService } from '../../services/contactService';
import { ticketService } from '../../services/ticketService';
import { atendimentoService, AtendimentoTicket } from '../../services/atendimentoService';
import { formatarDataHoraSegundos, formatarPhone, formatarValor, formatarCPFCNPJ } from '../../utils/formatToView';
import {
  calculateFaturaResumo,
  extractInvoicesFromResponse,
  FaturaResumo,
} from '../../utils/invoiceResumoUtils';

interface ClientOverviewProps {
  client: Client;
  onEdit?: () => void;
  onNewTicket?: () => void;
  onNewContract?: () => void;
  onNewInvoice?: () => void;
  onIniciarAtendimento?: () => void;
  onVerAtendimento?: () => void;
  podeIniciarAtendimento?: boolean;
  hasAtendimentoAtivo?: boolean;
  onViewInvoices?: () => void;
  onViewContracts?: () => void;
  onViewTickets?: () => void;
  onViewAtendimentos?: () => void;
  onRefresh?: () => void;
}

interface ClientStats {
  activeContracts: number;
  totalContracts: number;
  openTickets: number;
  totalAtendimentos: number;
  atendimentosAguardando: number;
  atendimentosEmAtendimento: number;
  faturaResumo: FaturaResumo;
}

const formatDate = (dateString: string | Date | undefined) => {
  if (!dateString) return '—';
  const d =
    typeof dateString === 'string' && dateString.indexOf('T') === -1
      ? new Date(`${dateString}T12:00:00`)
      : new Date(dateString);
  return d.toLocaleDateString('pt-BR');
};

const TIPO_CLIENTE_LABELS: Record<string, string> = {
  '01': '01 - Comercial',
  '02': '02 - Industrial',
  '03': '03 - Residencial/Pessoa Física',
  '04': '04 - Produtor Rural',
  '05': '05 - Órgão da administração pública estadual ICMS 107/95',
  '06': '06 - Prestador de serviço de telecomunicação ICMS 17/13',
  '07': '07 - Missões Diplomáticas ICMS 158/94',
  '08': '08 - Igrejas e Templos de qualquer natureza',
  '99': '99 - Outros não especificados anteriormente',
};

const formatTipoCliente = (tipo?: string) => {
  if (!tipo) return '—';
  return TIPO_CLIENTE_LABELS[tipo] ?? tipo;
};

const formatContribuinteIcms = (value?: number) => {
  switch (Number(value)) {
    case 1:
      return '1 - Contribuinte ICMS';
    case 2:
      return '2 - Isento de Inscrição ICMS';
    case 9:
      return '9 - Não contribuinte ICMS';
    default:
      return '—';
  }
};

interface StatCardProps {
  label: string;
  value: string | number;
  subtitle?: string;
  icon: React.ReactNode;
  color: string;
  onClick?: () => void;
  warning?: string;
}

const StatCard: React.FC<StatCardProps> = ({ label, value, subtitle, icon, color, onClick, warning }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  return (
    <Paper
      elevation={0}
      onClick={onClick}
      sx={{
        p: 2,
        borderRadius: 2.5,
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: 'background.paper',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.2s ease-in-out',
        ...(isDark && { backgroundImage: 'none' }),
        '&:hover': onClick
          ? {
            transform: 'translateY(-2px)',
            boxShadow: isDark ? `0 8px 24px ${alpha(color, 0.16)}` : `0 8px 20px ${alpha(color, 0.12)}`,
            borderColor: alpha(color, 0.5),
          }
          : undefined,
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: alpha(color, 0.12),
            color,
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={500}>
            {label}
          </Typography>
          <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1.2, mt: 0.25 }}>
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary" display="block" noWrap>
              {subtitle}
            </Typography>
          )}
          {warning && (
            <Typography variant="caption" color="warning.main" display="block" sx={{ mt: 0.5, fontWeight: 500 }}>
              {warning}
            </Typography>
          )}
        </Box>
        {onClick && (
          <ChevronRight sx={{ fontSize: 18, color: 'text.disabled', mt: 0.5 }} />
        )}
      </Stack>
    </Paper>
  );
};

interface InfoFieldProps {
  label: string;
  value: React.ReactNode;
  noWrap?: boolean;
}

const InfoField: React.FC<InfoFieldProps> = ({ label, value, noWrap = true }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.25, fontWeight: 500 }}>
      {label}
    </Typography>
    {typeof value === 'string' ? (
      <Typography variant="body2" fontWeight={600} noWrap={noWrap} title={value}>
        {value}
      </Typography>
    ) : (
      value
    )}
  </Box>
);

interface SectionCardProps {
  title: string;
  icon?: React.ReactNode;
  action?: React.ReactNode;
  children: React.ReactNode;
  highlight?: boolean;
}

const SectionCard: React.FC<SectionCardProps> = ({ title, icon, action, children, highlight }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2.5,
        border: `1px solid ${highlight ? alpha(theme.palette.primary.main, 0.35) : theme.palette.divider}`,
        overflow: 'hidden',
        bgcolor: 'background.paper',
        ...(isDark && { backgroundImage: 'none' }),
        boxShadow: highlight
          ? isDark
            ? `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}`
            : `0 4px 16px ${alpha(theme.palette.primary.main, 0.06)}`
          : 'none',
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: `1px solid ${theme.palette.divider}`,
          bgcolor: highlight
            ? alpha(theme.palette.primary.main, isDark ? 0.12 : 0.04)
            : alpha(theme.palette.primary.main, isDark ? 0.05 : 0.02),
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          {icon}
          <Typography variant="subtitle2" fontWeight={700}>
            {title}
          </Typography>
        </Stack>
        {action}
      </Box>
      <Box sx={{ p: 2 }}>{children}</Box>
    </Paper>
  );
};

const ClientOverview: React.FC<ClientOverviewProps> = ({
  client,
  onEdit,
  onNewTicket,
  onNewContract,
  onNewInvoice,
  onIniciarAtendimento,
  onVerAtendimento,
  podeIniciarAtendimento = false,
  hasAtendimentoAtivo = false,
  onViewInvoices,
  onViewContracts,
  onViewTickets,
  onViewAtendimentos,
  onRefresh,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // Estados de dados
  const [stats, setStats] = useState<ClientStats | null>(null);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Estados da Central do Assinante
  const [permitirAcesso, setPermitirAcesso] = useState<boolean>(Boolean(client.permitir_acesso_central));
  const [centralPassword, setCentralPassword] = useState<string>('');
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [savingCentral, setSavingCentral] = useState<boolean>(false);
  const [centralError, setCentralError] = useState<string | null>(null);
  const generatedPasswordOnce = useRef(false);

  // Snackbar de feedback
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'info' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Atualizar estado de permitirAcesso quando o cliente mudar
  useEffect(() => {
    setPermitirAcesso(Boolean(client.permitir_acesso_central));
    setCentralPassword('');
    generatedPasswordOnce.current = false;
  }, [client.id, client.permitir_acesso_central]);

  const loadClientData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [
        contractsResponse,
        invoicesResponse,
        contactsResponse,
        ticketsResponse,
        atendimentosResponse,
      ] = await Promise.allSettled([
        contractService.getByClientId(client.id),
        invoiceService.getInvoicesByClient(client.id),
        contactService.getByClientId(client.id),
        ticketService.getStatsForClient(client.id),
        atendimentoService.getTickets({ cliente_id: client.id }),
      ]);

      let contracts: Contract[] = [];
      if (contractsResponse.status === 'fulfilled' && contractsResponse.value.success) {
        contracts = contractsResponse.value.data || [];
      }

      let invoices: Invoice[] = [];
      if (invoicesResponse.status === 'fulfilled') {
        invoices = extractInvoicesFromResponse(invoicesResponse.value);
      }

      const faturaResumo = calculateFaturaResumo(invoices);

      let contactsList: Contact[] = [];
      if (contactsResponse.status === 'fulfilled' && contactsResponse.value.success) {
        contactsList = contactsResponse.value.data || [];
      }

      let openTicketsCount = 0;
      if (ticketsResponse.status === 'fulfilled' && ticketsResponse.value.success) {
        const ticketStats = ticketsResponse.value.data;
        openTicketsCount = (ticketStats?.aberto || 0) + (ticketStats?.em_andamento || 0);
      }

      let atendimentosList: AtendimentoTicket[] = [];
      if (atendimentosResponse.status === 'fulfilled') {
        atendimentosList = atendimentosResponse.value || [];
      }
      const atendimentosAguardandoCount = atendimentosList.filter((t) => t.status === 'pendente').length;
      const atendimentosEmAtendimentoCount = atendimentosList.filter((t) => t.status === 'em_atendimento').length;

      const activeContractsCount = contracts.filter((c) => c.status === 'ativo').length;

      setStats({
        activeContracts: activeContractsCount,
        totalContracts: contracts.length,
        openTickets: openTicketsCount,
        totalAtendimentos: atendimentosList.length,
        atendimentosAguardando: atendimentosAguardandoCount,
        atendimentosEmAtendimento: atendimentosEmAtendimentoCount,
        faturaResumo,
      });
      setContacts(contactsList);
    } catch (err) {
      console.error('Erro ao carregar dados do cliente:', err);
      setError('Não foi possível carregar as informações do cliente.');
    } finally {
      setLoading(false);
    }
  }, [client.id]);

  useEffect(() => {
    loadClientData();
  }, [loadClientData]);

  const handleRefresh = useCallback(() => {
    loadClientData();
    onRefresh?.();
  }, [loadClientData, onRefresh]);

  // Função para gerar senha igual ao ClientForm
  const handleGenerateCentralPassword = () => {
    const cpfCnpjValue = client.cpf_cnpj?.replace(/\D/g, '');

    if (cpfCnpjValue && !generatedPasswordOnce.current) {
      setCentralPassword(cpfCnpjValue);
      generatedPasswordOnce.current = true;
      setShowPassword(true);
      setCentralError(null);
    } else {
      const randomPassword = Math.floor(10000000 + Math.random() * 90000000).toString();
      setCentralPassword(randomPassword);
      generatedPasswordOnce.current = true;
      setShowPassword(true);
      setCentralError(null);
    }
  };

  // Função para copiar texto para o clipboard
  const handleCopyToClipboard = (text: string, label: string) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setSnackbar({
      open: true,
      message: `${label} copiado para a área de transferência!`,
      severity: 'success',
    });
  };

  // Função para salvar as credenciais da Central do Assinante
  const handleSaveCentralAccess = async () => {
    setCentralError(null);

    // Validações
    if (permitirAcesso) {
      if (centralPassword) {
        if (centralPassword.length < 6 || centralPassword.length > 20) {
          setCentralError('A senha deve ter entre 6 e 20 caracteres.');
          return;
        }
      }
    }

    try {
      setSavingCentral(true);

      const updatePayload: Partial<Client> = {
        ...client,
        permitir_acesso_central: permitirAcesso,
      };

      if (centralPassword && centralPassword.trim() !== '') {
        updatePayload.central_password = centralPassword.trim();
      }

      await clientService.update(client.id, updatePayload);

      setSnackbar({
        open: true,
        message: 'Acesso à Central do Assinante atualizado com sucesso!',
        severity: 'success',
      });

      // Atualiza os dados do cliente e limpa a senha local
      setCentralPassword('');
      generatedPasswordOnce.current = false;
      onRefresh?.();
    } catch (err: any) {
      console.error('Erro ao atualizar Central do Assinante:', err);
      const msg = err.response?.data?.message || 'Não foi possível salvar as configurações da Central.';
      setCentralError(msg);
      setSnackbar({
        open: true,
        message: msg,
        severity: 'error',
      });
    } finally {
      setSavingCentral(false);
    }
  };

  const contractWarning =
    client.status === 'ativo' && stats?.activeContracts === 0
      ? 'Cliente ativo sem contrato ativo'
      : client.status === 'inativo' && (stats?.activeContracts || 0) > 0
        ? 'Cliente inativo com contrato ativo'
        : undefined;

  const faturaResumo = stats?.faturaResumo;
  const pendingInvoicesCount = (faturaResumo?.pendentes || 0) + (faturaResumo?.vencidas || 0);

  const invoiceWarning =
    client.status === 'inativo' && pendingInvoicesCount > 0
      ? 'Cliente inativo com faturas em aberto'
      : undefined;

  const atendimentosAbertos = (stats?.atendimentosAguardando || 0) + (stats?.atendimentosEmAtendimento || 0);

  const atendimentosSubtitle = (() => {
    const aguardando = stats?.atendimentosAguardando || 0;
    const emAtendimento = stats?.atendimentosEmAtendimento || 0;
    const partes: string[] = [];
    if (aguardando > 0) partes.push(`${aguardando} aguardando`);
    if (emAtendimento > 0) partes.push(`${emAtendimento} em atendimento`);
    if (partes.length > 0) return partes.join(' · ');
    return (stats?.totalAtendimentos || 0) > 0 ? 'Nenhum em aberto' : 'Nenhum atendimento';
  })();

  const temAlteracaoCentral =
    permitirAcesso !== Boolean(client.permitir_acesso_central) || centralPassword.trim().length > 0;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
        <CircularProgress size={36} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Barra superior de Ações Rápidas */}
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={1}
        sx={{ mb: 2 }}
      >
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {onEdit && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<Edit sx={{ fontSize: 16 }} />}
              onClick={onEdit}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
            >
              Editar cadastro
            </Button>
          )}
          {onNewContract && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<Assignment sx={{ fontSize: 16 }} />}
              onClick={onNewContract}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
            >
              Novo contrato
            </Button>
          )}
          {onNewTicket && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<SupportAgent sx={{ fontSize: 16 }} />}
              onClick={onNewTicket}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
            >
              Novo chamado
            </Button>
          )}
          {onNewInvoice && (
            <Button
              size="small"
              variant="contained"
              startIcon={<Add sx={{ fontSize: 16 }} />}
              onClick={onNewInvoice}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
            >
              Nova Fatura
            </Button>
          )}
          {podeIniciarAtendimento && hasAtendimentoAtivo && onVerAtendimento && (
            <Button
              size="small"
              variant="contained"
              color="success"
              startIcon={<ChatIcon sx={{ fontSize: 16 }} />}
              onClick={onVerAtendimento}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
            >
              Ver Atendimento
            </Button>
          )}
          {podeIniciarAtendimento && !hasAtendimentoAtivo && onIniciarAtendimento && (
            <Button
              size="small"
              variant="contained"
              startIcon={<ChatIcon sx={{ fontSize: 16 }} />}
              onClick={onIniciarAtendimento}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
            >
              Iniciar Atendimento
            </Button>
          )}
        </Stack>
        <Tooltip title="Atualizar dados">
          <IconButton
            size="small"
            onClick={handleRefresh}
            sx={{
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
              p: 0.75,
            }}
          >
            <Refresh sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2.5 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Alerta de cliente inativo */}
      {client.status === 'inativo' && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2.5 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={{ xs: 1.5, sm: 3 }}
            divider={<Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />}
          >
            <Box>
              <Typography variant="caption" color="text.secondary">Desativado em</Typography>
              <Typography variant="body2" fontWeight={600}>
                {formatarDataHoraSegundos(client.data_desativacao || '')}
              </Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary">Motivo</Typography>
              <Typography variant="body2" fontWeight={500}>
                {client.motivo_desativacao || 'Não informado'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Por</Typography>
              <Typography variant="body2" fontWeight={600}>
                {client.usuario_desativou?.nome || client.desativado_por || 'Sistema'}
              </Typography>
            </Box>
          </Stack>
        </Alert>
      )}

      {/* KPIs Cards */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Contratos ativos"
            value={stats?.activeContracts || 0}
            subtitle={`${stats?.totalContracts || 0} no total`}
            icon={<Assignment sx={{ fontSize: 22 }} />}
            color={theme.palette.primary.main}
            onClick={onViewContracts}
            warning={contractWarning}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Faturas pendentes"
            value={pendingInvoicesCount}
            subtitle={
              pendingInvoicesCount
                ? `${faturaResumo?.pendentes || 0} abertas · ${faturaResumo?.vencidas || 0} vencidas`
                : 'Sem débitos pendentes'
            }
            icon={<Receipt sx={{ fontSize: 22 }} />}
            color={
              pendingInvoicesCount
                ? theme.palette.warning.main
                : theme.palette.success.main
            }
            onClick={onViewInvoices}
            warning={invoiceWarning}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Chamados abertos"
            value={stats?.openTickets || 0}
            subtitle="Aguardando atendimento"
            icon={<ContactPhone sx={{ fontSize: 22 }} />}
            color={
              stats?.openTickets
                ? theme.palette.error.main
                : theme.palette.success.main
            }
            onClick={onViewTickets}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Atendimentos"
            value={stats?.totalAtendimentos || 0}
            subtitle={atendimentosSubtitle}
            icon={<HeadsetMic sx={{ fontSize: 22 }} />}
            color={
              atendimentosAbertos
                ? theme.palette.info.main
                : theme.palette.success.main
            }
            onClick={onViewAtendimentos}
          />
        </Grid>
      </Grid>

      {/* Conteúdo Principal Dividido em 2 Colunas */}
      <Grid container spacing={2}>
        {/* Coluna Principal (Esquerda - 7 cols): Informações Cadastrais e Endereço */}
        <Grid item xs={12} md={7}>
          <Stack spacing={2}>
            {/* Informações do Cadastro */}
            <SectionCard
              title="Informações do Cadastro"
              icon={<Person sx={{ fontSize: 20, color: 'primary.main' }} />}
              action={
                onEdit ? (
                  <Button
                    size="small"
                    startIcon={<Edit sx={{ fontSize: 14 }} />}
                    onClick={onEdit}
                    sx={{ textTransform: 'none', fontSize: '0.75rem', fontWeight: 600 }}
                  >
                    Editar
                  </Button>
                ) : undefined
              }
            >
              <Grid container spacing={2}>
                <Grid item xs={6} sm={4}>
                  <InfoField label="Data de cadastro" value={formatarDataHoraSegundos(client.createdAt)} />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <InfoField
                    label="Tipo de pessoa"
                    value={
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Chip
                          label={client.tipo_pessoa === 'pf' ? 'Pessoa Física' : 'Pessoa Jurídica'}
                          size="small"
                          color={client.tipo_pessoa === 'pf' ? 'primary' : 'secondary'}
                          variant="outlined"
                          sx={{ height: 22, fontSize: '0.7rem', fontWeight: 600 }}
                        />
                      </Stack>
                    }
                  />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <InfoField
                    label="CPF / CNPJ"
                    value={
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Typography variant="body2" fontWeight={700}>
                          {formatarCPFCNPJ(client.cpf_cnpj)}
                        </Typography>
                        <Tooltip title="Copiar documento">
                          <IconButton
                            size="small"
                            onClick={() => handleCopyToClipboard(client.cpf_cnpj?.replace(/\D/g, '') || '', 'CPF/CNPJ')}
                            sx={{ p: 0.25 }}
                          >
                            <ContentCopy sx={{ fontSize: 13 }} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    }
                  />
                </Grid>

                <Grid item xs={6} sm={4}>
                  <InfoField label="RG / Inscrição Estadual" value={client.rg_ie || '—'} />
                </Grid>
                {client.data_nascimento && (
                  <Grid item xs={6} sm={4}>
                    <InfoField label="Data de nascimento" value={formatDate(client.data_nascimento)} />
                  </Grid>
                )}
                <Grid item xs={12} sm={client.data_nascimento ? 4 : 8}>
                  <InfoField
                    label="Contribuinte ICMS"
                    value={formatContribuinteIcms(client.contribuinte_icms)}
                    noWrap={false}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <InfoField label="Tipo de cliente" value={formatTipoCliente(client.tipo_cliente)} />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <InfoField
                    label="Escritório / Filial"
                    value={
                      client.escritorio?.identificacao
                        ? client.escritorio.identificacao
                        : String(client.escritorio_id || '—')
                    }
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <InfoField
                    label="Vendedor"
                    value={
                      client.vendedor?.nome
                        ? `${client.vendedor.nome} (#${client.vendedor_id})`
                        : String(client.vendedor_id || '—')
                    }
                  />
                </Grid>

                <Grid item xs={6} sm={4}>
                  <InfoField
                    label="Indicado por"
                    value={
                      client.cliente_indicador?.nome
                        ? `${client.cliente_indicador.nome} (#${client.indicado_por})`
                        : client.indicado_por
                          ? String(client.indicado_por)
                          : 'Direto'
                    }
                  />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <InfoField label="Como conheceu" value={client.como_conheceu || '—'} />
                </Grid>
                {client.tipo_moradia && (
                  <Grid item xs={6} sm={4}>
                    <InfoField
                      label="Tipo de moradia"
                      value={client.tipo_moradia === 'propria' ? 'Própria' : 'Alugado'}
                    />
                  </Grid>
                )}

                {client.nome_mae && (
                  <Grid item xs={12} sm={6}>
                    <InfoField label="Nome da mãe" value={client.nome_mae} />
                  </Grid>
                )}
                {client.nome_pai && (
                  <Grid item xs={12} sm={6}>
                    <InfoField label="Nome do pai" value={client.nome_pai} />
                  </Grid>
                )}
              </Grid>
            </SectionCard>

            {/* Endereço */}
            <SectionCard
              title="Endereço do Cliente"
              icon={<LocationOn sx={{ fontSize: 20, color: 'primary.main' }} />}
              action={
                <Button
                  size="small"
                  target="_blank"
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                    `${client.endereco}, ${client.numero || 'S/N'}, ${client.bairro}, ${client.cidade} - ${client.estado}, ${client.cep}`
                  )}`}
                  startIcon={<OpenInNew sx={{ fontSize: 14 }} />}
                  sx={{ textTransform: 'none', fontSize: '0.75rem', fontWeight: 600 }}
                >
                  Abrir no Maps
                </Button>
              }
            >
              <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.95rem' }}>
                {client.endereco}
                {client.numero ? `, nº ${client.numero}` : ', S/N'}
                {client.complemento ? ` — ${client.complemento}` : ''}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {client.bairro ? `${client.bairro} · ` : ''}
                {client.cidade} - {client.estado} · CEP: {client.cep}
              </Typography>
              {client.referencia && (
                <Box
                  sx={{
                    mt: 1.5,
                    p: 1.25,
                    borderRadius: 1.5,
                    bgcolor: alpha(theme.palette.warning.main, isDark ? 0.08 : 0.04),
                    border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                  }}
                >
                  <Typography variant="caption" color="warning.main" fontWeight={700} display="block">
                    PONTO DE REFERÊNCIA:
                  </Typography>
                  <Typography variant="body2" color="text.primary">
                    {client.referencia}
                  </Typography>
                </Box>
              )}
            </SectionCard>
          </Stack>
        </Grid>

        {/* Coluna Lateral (Direita - 5 cols): Financeiro, Central do Assinante e Contatos */}
        <Grid item xs={12} md={5}>
          <Stack spacing={2}>
            {/* Resumo Financeiro */}
            <SectionCard
              title="Resumo Financeiro"
              icon={<AccountBalanceWallet sx={{ fontSize: 20, color: 'success.main' }} />}
              action={
                onViewInvoices ? (
                  <Button
                    size="small"
                    onClick={onViewInvoices}
                    sx={{ textTransform: 'none', fontSize: '0.75rem', fontWeight: 600 }}
                  >
                    Ver todas
                  </Button>
                ) : undefined
              }
            >
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <Typography variant="caption" color="text.secondary" fontWeight={500}>Total pago</Typography>
                  <Typography variant="h6" fontWeight={700} color="success.main" sx={{ lineHeight: 1.2 }}>
                    {formatarValor(faturaResumo?.valorPago || 0)}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="caption" color="text.secondary" fontWeight={500}>Faturas pagas</Typography>
                  <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                    {faturaResumo?.pagas || 0}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="caption" color="text.secondary" fontWeight={500}>Vencidas</Typography>
                  <Typography
                    variant="h6"
                    fontWeight={700}
                    color={(faturaResumo?.vencidas || 0) > 0 ? 'error.main' : 'text.primary'}
                    sx={{ lineHeight: 1.2 }}
                  >
                    {faturaResumo?.vencidas || 0}
                  </Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 1.5 }} />

              <Stack spacing={1}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="text.secondary">Último pagamento</Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {formatDate(faturaResumo?.lastPayment)}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color={pendingInvoicesCount > 0 ? 'warning.dark' : 'text.secondary'}>
                    Próximo vencimento
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={700}
                    color={pendingInvoicesCount > 0 ? 'warning.dark' : 'text.primary'}
                  >
                    {formatDate(faturaResumo?.nextDueDate)}
                  </Typography>
                </Stack>
              </Stack>
            </SectionCard>

            {/* Contatos */}
            <SectionCard
              title="Contatos"
              icon={<Phone sx={{ fontSize: 20, color: 'primary.main' }} />}
              action={
                <Chip
                  label={`${contacts.length} cadastrado${contacts.length === 1 ? '' : 's'}`}
                  size="small"
                  variant="outlined"
                  sx={{ height: 22, fontSize: '0.7rem' }}
                />
              }
            >
              {contacts.length > 0 ? (
                <List disablePadding>
                  {contacts.map((contact, idx) => {
                    const rawNumber = contact.numero?.replace(/\D/g, '') || '';
                    return (
                      <ListItem
                        key={contact.id}
                        disablePadding
                        sx={{
                          py: 1,
                          borderBottom:
                            idx < contacts.length - 1 ? `1px solid ${theme.palette.divider}` : 'none',
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          {contact.tipo === 'email' ? (
                            <Email sx={{ fontSize: 20, color: 'info.main' }} />
                          ) : contact.is_whatsapp ? (
                            <WhatsApp sx={{ fontSize: 20, color: 'success.main' }} />
                          ) : (
                            <Phone sx={{ fontSize: 20, color: 'primary.main' }} />
                          )}
                        </ListItemIcon>
                        <ListItemText
                          primaryTypographyProps={{ component: 'div' }}
                          secondaryTypographyProps={{ component: 'div' }}
                          primary={
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Typography variant="body2" fontWeight={600}>
                                {contact.email || formatarPhone(contact.numero || '')}
                              </Typography>
                              {contact.tipo !== 'email' && contact.is_whatsapp && rawNumber && (
                                <Tooltip title="Abrir conversa no WhatsApp">
                                  <IconButton
                                    size="small"
                                    color="success"
                                    target="_blank"
                                    href={`https://wa.me/55${rawNumber}`}
                                    sx={{ p: 0.25 }}
                                  >
                                    <WhatsApp sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </Tooltip>
                              )}
                              <Tooltip title="Copiar">
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    handleCopyToClipboard(
                                      contact.tipo === 'email' ? contact.email || '' : rawNumber,
                                      'Contato'
                                    )
                                  }
                                  sx={{ p: 0.25 }}
                                >
                                  <ContentCopy sx={{ fontSize: 13 }} />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          }
                          secondary={
                            <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }} flexWrap="wrap" gap={0.5}>
                              {contact.nome && (
                                <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                                  ({contact.nome})
                                </Typography>
                              )}
                              {contact.is_whatsapp && (
                                <Chip
                                  label="WhatsApp"
                                  size="small"
                                  color="success"
                                  variant="outlined"
                                  sx={{ height: 18, fontSize: '0.65rem', fontWeight: 600 }}
                                />
                              )}
                              {contact.recebe_notificacao && (
                                <Chip
                                  label="Notificações"
                                  size="small"
                                  color="info"
                                  variant="outlined"
                                  sx={{ height: 18, fontSize: '0.65rem' }}
                                />
                              )}
                              {contact.is_default && (
                                <Chip
                                  label="Principal"
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                  sx={{ height: 18, fontSize: '0.65rem' }}
                                />
                              )}
                            </Stack>
                          }
                        />
                      </ListItem>
                    );
                  })}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Nenhum contato cadastrado para este cliente.
                </Typography>
              )}
            </SectionCard>

            {/* SEÇÃO DA CENTRAL DO ASSINANTE (substituindo chamados recentes) */}
            <SectionCard
              title="Central do Assinante"
              highlight
              icon={<VpnKey sx={{ fontSize: 20, color: 'primary.main' }} />}
              action={
                <Chip
                  icon={permitirAcesso ? <CheckCircle sx={{ fontSize: '14px !important' }} /> : <Lock sx={{ fontSize: '14px !important' }} />}
                  label={permitirAcesso ? 'Acesso Liberado' : 'Acesso Bloqueado'}
                  size="small"
                  color={permitirAcesso ? 'success' : 'default'}
                  variant={permitirAcesso ? 'filled' : 'outlined'}
                  sx={{ fontWeight: 600, fontSize: '0.75rem', height: 24 }}
                />
              }
            >
              <Stack spacing={2}>
                {centralError && (
                  <Alert severity="error" sx={{ borderRadius: 2 }} onClose={() => setCentralError(null)}>
                    {centralError}
                  </Alert>
                )}

                {/* Switch de Ativação */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.divider}`,
                    bgcolor: alpha(theme.palette.background.default, 0.5),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Box sx={{ pr: 1.5 }}>
                    <Typography variant="body2" fontWeight={600}>
                      Permitir acesso à Central
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Login no portal e app móvel
                    </Typography>
                  </Box>
                  <Switch
                    checked={permitirAcesso}
                    onChange={(e) => {
                      setPermitirAcesso(e.target.checked);
                      setCentralError(null);
                    }}
                    color="primary"
                  />
                </Paper>

                {/* Configuração de Senha */}
                <TextField
                  fullWidth
                  size="small"
                  label="Senha da Central"
                  type={showPassword ? 'text' : 'password'}
                  value={centralPassword}
                  onChange={(e) => {
                    setCentralPassword(e.target.value);
                    setCentralError(null);
                  }}
                  placeholder={permitirAcesso ? 'Digite ou gere a senha' : 'Acesso desabilitado'}
                  disabled={!permitirAcesso}
                  inputProps={{ maxLength: 20, autoComplete: 'new-password' }}
                  helperText={
                    permitirAcesso
                      ? centralPassword
                        ? `${centralPassword.length}/20 caracteres`
                        : 'Deixe vazio para manter atual ou gere nova'
                      : 'Ative o switch acima para gerenciar a senha'
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock sx={{ fontSize: 18, color: permitirAcesso ? 'text.secondary' : 'text.disabled' }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="alternar visibilidade da senha"
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          size="small"
                          disabled={!permitirAcesso}
                          sx={{ mr: 0.5 }}
                          title={showPassword ? 'Ocultar senha' : 'Ver senha'}
                        >
                          {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>

                        {centralPassword && (
                          <Tooltip title="Copiar senha">
                            <IconButton
                              size="small"
                              onClick={() => handleCopyToClipboard(centralPassword, 'Senha da Central')}
                              sx={{ mr: 0.5 }}
                            >
                              <ContentCopy fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}

                        <Button
                          size="small"
                          onClick={handleGenerateCentralPassword}
                          variant="outlined"
                          disabled={!permitirAcesso}
                          sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            px: 1.5,
                            height: 30,
                            borderRadius: 1.5,
                          }}
                        >
                          Gerar
                        </Button>
                      </InputAdornment>
                    ),
                  }}
                />

                {/* Informações de Login para o Atendente */}
                <Box
                  sx={{
                    p: 1.25,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.info.main, isDark ? 0.08 : 0.04),
                    border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 1,
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <InfoOutlined sx={{ fontSize: 18, color: 'info.main' }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Login do cliente:
                      </Typography>
                      <Typography variant="body2" fontWeight={700} color="text.primary">
                        {formatarCPFCNPJ(client.cpf_cnpj)}
                      </Typography>
                    </Box>
                  </Stack>

                  <Tooltip title="Copiar CPF/CNPJ">
                    <Button
                      size="small"
                      variant="text"
                      startIcon={<ContentCopy sx={{ fontSize: 13 }} />}
                      onClick={() => handleCopyToClipboard(client.cpf_cnpj?.replace(/\D/g, '') || '', 'CPF/CNPJ')}
                      sx={{ textTransform: 'none', fontSize: '0.75rem', p: 0.5 }}
                    >
                      Copiar
                    </Button>
                  </Tooltip>
                </Box>

                {/* Botão de Salvar Alterações da Central */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 0.5 }}>
                  <Button
                    variant="contained"
                    size="small"
                    color="primary"
                    startIcon={savingCentral ? <CircularProgress size={16} color="inherit" /> : <Save sx={{ fontSize: 18 }} />}
                    onClick={handleSaveCentralAccess}
                    disabled={savingCentral || !temAlteracaoCentral}
                    sx={{
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 2,
                    }}
                  >
                    {savingCentral ? 'Salvando...' : 'Salvar Acesso'}
                  </Button>
                </Box>
              </Stack>
            </SectionCard>
          </Stack>
        </Grid>
      </Grid>

      {/* Observações Internas */}
      {client.observacoes && (
        <Paper
          elevation={0}
          sx={{
            mt: 2,
            p: 2,
            borderRadius: 2.5,
            border: `1px solid ${alpha(theme.palette.info.main, 0.25)}`,
            bgcolor: alpha(theme.palette.info.main, isDark ? 0.08 : 0.03),
          }}
        >
          <Typography variant="caption" color="info.main" fontWeight={700} display="block" sx={{ mb: 0.5 }}>
            OBSERVAÇÕES INTERNAS DO CLIENTE
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
            {client.observacoes}
          </Typography>
        </Paper>
      )}

      {/* Snackbar para feedback de ações */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%', borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ClientOverview;
